import React, { useState, useEffect } from "react";
import { Building2, Briefcase, PlusCircle, LogOut, Eye, FileText, Download } from "lucide-react";
import { /* Link, */ useNavigate } from "react-router-dom";
import "./HrDashboard.css";

export default function HrDashboard() {
  const navigate = useNavigate();
  const [view, setView] = useState("overview"); 
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);

  // Load jobs and applications from localStorage
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);

  const [form, setForm] = useState({
    title: "",
    location: "",
    type: "Full-time",
    description: ""
  });

  useEffect(() => {
    // Load jobs from localStorage
    const storedJobs = JSON.parse(localStorage.getItem("jobs") || "[]");
    if (storedJobs.length === 0) {
      // Initialize with default jobs
      const defaultJobs = [
        { id: 1, title: "Sales Consultant", location: "Durban, KZN", type: "Full-time", description: "Engage with customers and drive sales.", postedDate: new Date().toLocaleDateString() },
        { id: 2, title: "Store Manager", location: "Cape Town, WC", type: "Full-time", description: "Lead and manage store operations.", postedDate: new Date().toLocaleDateString() },
        { id: 3, title: "Kiosk Assistant", location: "Kimberley, NC", type: "Part-time", description: "Assist customers at kiosk locations.", postedDate: new Date().toLocaleDateString() },
      ];
      localStorage.setItem("jobs", JSON.stringify(defaultJobs));
      setJobs(defaultJobs);
    } else {
      setJobs(storedJobs);
    }

    // Load applications from localStorage
    const storedApps = JSON.parse(localStorage.getItem("applications") || "[]");
    setApplications(storedApps);
  }, []);

  const handlePostJob = (e) => {
    e.preventDefault();

    if (form.title && form.location && form.description) {
      const newJob = {
        id: Date.now(),
        title: form.title,
        location: form.location,
        type: form.type,
        description: form.description,
        postedDate: new Date().toLocaleDateString()
      };
      
      const updatedJobs = [...jobs, newJob];
      setJobs(updatedJobs);
      localStorage.setItem("jobs", JSON.stringify(updatedJobs));
      
      setForm({ title: "", location: "", type: "Full-time", description: "" });
      alert("Job posted successfully!");
    } else {
      alert("Please fill in all fields.");
    }
  };

  const handleViewApplication = (app) => {
    setSelectedApplication(app);
    setView("viewApplication");
  };

  const handleUpdateStatus = (appId, newStatus) => {
    const updatedApps = applications.map(app =>
      app.id === appId ? { ...app, status: newStatus } : app
    );
    setApplications(updatedApps);
    localStorage.setItem("applications", JSON.stringify(updatedApps));
    
    // Update selected application if it's currently being viewed
    if (selectedApplication && selectedApplication.id === appId) {
      setSelectedApplication({ ...selectedApplication, status: newStatus });
    }
    
    alert(`Application status updated to ${newStatus}`);
  };

  const handleLogout = () => {
    alert("Logged out of HR Portal.");
    navigate("/");
  };

  const getApplicationCountForJob = (jobId) => {
    return applications.filter(app => String(app.jobId) === String(jobId)).length;
  };

  const handleViewJobApplications = (job) => {
    setSelectedJob(job);
    setView("viewJobApplications");
  };

  // Overview View
  if (view === "overview") {
    return (
      <div className="hr-root">
        <nav className="hr-nav">
          <div className="container nav-inner">
            <div className="brand">
              <Building2 className="brand-icon" />
              <span className="brand-text">HR Dashboard</span>
            </div>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut className="icon-small" />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <div className="container hr-main">
          <div className="hr-grid">
            {/* Post Job */}
            <div className="card">
              <h3 className="card-title">
                <PlusCircle className="title-icon" />
                Post a New Job
              </h3>
              <form onSubmit={handlePostJob} className="job-form">
                <div className="form-field">
                  <label className="field-label">Job Title</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">Location</label>
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="form-input"
                    placeholder="e.g., Durban, KZN"
                    required
                  />
                </div>

                <div className="form-field">
                  <label className="field-label">Job Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                    className="form-select"
                  >
                    <option>Full-time</option>
                    <option>Part-time</option>
                    <option>Contract</option>
                    <option>Internship</option>
                  </select>
                </div>

                <div className="form-field">
                  <label className="field-label">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="form-textarea"
                    rows="3"
                    required
                  />
                </div>

                <button type="submit" className="submit-btn">
                  Post Job
                </button>
              </form>
            </div>

            {/* Applications */}
            <div className="card">
              <h3 className="card-title">
                <Briefcase className="title-icon" />
                Recent Applications ({applications.length})
              </h3>
              {applications.length === 0 ? (
                <div className="empty-state">
                  <Briefcase className="empty-icon" />
                  <p>No applications received yet</p>
                </div>
              ) : (
                <div className="applications-list">
                  {applications.slice(0, 5).map((app) => (
                    <div
                      key={app.id}
                      className="application-item"
                      onClick={() => handleViewApplication(app)}
                    >
                      <div className="app-info">
                        <h4 className="app-job-title">{app.jobTitle}</h4>
                        <p className="app-applicant">
                          {app.applicantData?.fullName || "Applicant"}
                        </p>
                        <p className="app-date">Applied: {app.appliedDate}</p>
                      </div>
                      <div className="app-actions">
                        <span className={`status-badge status-${app.status.toLowerCase().replace(' ', '-')}`}>
                          {app.status}
                        </span>
                        <Eye className="view-icon" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Active Jobs */}
            <div className="card full-width">
              <h3 className="card-title">
                <Briefcase className="title-icon" />
                Active Job Listings ({jobs.length})
              </h3>
              <div className="jobs-grid">
                {jobs.map((job) => (
                  <div key={job.id} className="job-card">
                    <h4 className="job-card-title">{job.title}</h4>
                    <p className="job-card-meta">{job.location} • {job.type}</p>
                    <p className="job-card-date">Posted: {job.postedDate}</p>
                    <p 
                      className="job-card-apps clickable"
                      onClick={() => handleViewJobApplications(job)}
                    >
                      {getApplicationCountForJob(job.id)} applications
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // View Application Detail
  if (view === "viewApplication" && selectedApplication) {
    return (
      <div className="hr-root">
        <nav className="hr-nav">
          <div className="container nav-inner">
            <button onClick={() => setView("overview")} className="back-btn">
              <Building2 className="brand-icon" />
              Back to Dashboard
            </button>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut className="icon-small" />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <div className="container application-detail">
          <div className="detail-card">
            <div className="detail-header">
              <div>
                <h2 className="detail-title">{selectedApplication.jobTitle}</h2>
                <p className="detail-meta">Application ID: {selectedApplication.id}</p>
                <p className="detail-meta">Submitted: {selectedApplication.appliedDate}</p>
              </div>
              <span className={`status-badge-large status-${selectedApplication.status.toLowerCase().replace(' ', '-')}`}>
                {selectedApplication.status}
              </span>
            </div>

            {/* Personal Information */}
            <section className="detail-section">
              <h3 className="section-title">Personal Information</h3>
              <div className="info-grid">
                <div className="info-item">
                  <p className="info-label">Full Name</p>
                  <p className="info-value">{selectedApplication.applicantData?.fullName}</p>
                </div>
                <div className="info-item">
                  <p className="info-label">Email</p>
                  <p className="info-value">{selectedApplication.applicantData?.email}</p>
                </div>
                <div className="info-item">
                  <p className="info-label">Phone</p>
                  <p className="info-value">{selectedApplication.applicantData?.phone}</p>
                </div>
                <div className="info-item">
                  <p className="info-label">ID Number</p>
                  <p className="info-value">{selectedApplication.applicantData?.idNumber}</p>
                </div>
                <div className="info-item full-width">
                  <p className="info-label">Address</p>
                  <p className="info-value">{selectedApplication.applicantData?.address}</p>
                </div>
              </div>
            </section>

            {/* Professional Background */}
            <section className="detail-section">
              <h3 className="section-title">Professional Background</h3>
              <div className="text-section">
                <div className="text-block">
                  <p className="text-label">Work Experience</p>
                  <p className="text-content">{selectedApplication.applicantData?.experience}</p>
                </div>
                <div className="text-block">
                  <p className="text-label">Education</p>
                  <p className="text-content">{selectedApplication.applicantData?.education}</p>
                </div>
              </div>
            </section>

            {/* Documents */}
            <section className="detail-section">
              <h3 className="section-title">Submitted Documents</h3>
              <div className="documents-list">
                {selectedApplication.documents?.cv && (
                  <div className="document-item">
                    <div className="doc-info">
                      <FileText className="doc-icon" />
                      <div>
                        <p className="doc-title">CV/Resume</p>
                        <p className="doc-filename">{selectedApplication.documents.cv}</p>
                      </div>
                    </div>
                    <button className="download-btn">
                      <Download className="icon-small" />
                      Download
                    </button>
                  </div>
                )}
                {selectedApplication.documents?.id && (
                  <div className="document-item">
                    <div className="doc-info">
                      <FileText className="doc-icon" />
                      <div>
                        <p className="doc-title">ID Document</p>
                        <p className="doc-filename">{selectedApplication.documents.id}</p>
                      </div>
                    </div>
                    <button className="download-btn">
                      <Download className="icon-small" />
                      Download
                    </button>
                  </div>
                )}
                {selectedApplication.documents?.certificates && (
                  <div className="document-item">
                    <div className="doc-info">
                      <FileText className="doc-icon" />
                      <div>
                        <p className="doc-title">Certificates</p>
                        <p className="doc-filename">{selectedApplication.documents.certificates}</p>
                      </div>
                    </div>
                    <button className="download-btn">
                      <Download className="icon-small" />
                      Download
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* Update Status */}
            <section className="detail-section">
              <h3 className="section-title">Update Application Status</h3>
              <div className="status-actions">
                <button
                  onClick={() => handleUpdateStatus(selectedApplication.id, "Pending")}
                  className="status-btn status-btn-pending"
                >
                  Pending
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedApplication.id, "Under Review")}
                  className="status-btn status-btn-review"
                >
                  Under Review
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedApplication.id, "Accepted")}
                  className="status-btn status-btn-accepted"
                >
                  Accepted
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedApplication.id, "Rejected")}
                  className="status-btn status-btn-rejected"
                >
                  Rejected
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    );
  }

  // View Job Applications
  if (view === "viewJobApplications" && selectedJob) {
    const jobApplications = applications.filter(app => String(app.jobId) === String(selectedJob.id));

    return (
      <div className="hr-root">
        <nav className="hr-nav">
          <div className="container nav-inner">
            <button onClick={() => setView("overview")} className="back-btn">
              <Building2 className="brand-icon" />
              Back to Dashboard
            </button>
            <button onClick={handleLogout} className="btn-logout">
              <LogOut className="icon-small" />
              <span>Logout</span>
            </button>
          </div>
        </nav>

        <div className="container application-detail">
          <div className="detail-card">
            <div className="detail-header">
              <div>
                <h2 className="detail-title">Applications for {selectedJob.title}</h2>
                <p className="detail-meta">{selectedJob.location} • {selectedJob.type}</p>
                <p className="detail-meta">{jobApplications.length} total applications</p>
              </div>
            </div>

            {jobApplications.length === 0 ? (
              <div className="empty-state">
                <Briefcase className="empty-icon" />
                <p>No applications received for this position yet</p>
              </div>
            ) : (
              <div className="applications-list">
                {jobApplications.map((app) => (
                  <div
                    key={app.id}
                    className="application-item"
                    onClick={() => handleViewApplication(app)}
                  >
                    <div className="app-info">
                      <h4 className="app-job-title">{app.applicantData?.fullName || "Applicant"}</h4>
                      <p className="app-applicant">{app.applicantData?.email || "No email"}</p>
                      <p className="app-applicant">{app.applicantData?.phone || "No phone"}</p>
                      <p className="app-date">Applied: {app.appliedDate}</p>
                    </div>
                    <div className="app-actions">
                      <span className={`status-badge status-${app.status.toLowerCase().replace(' ', '-')}`}>
                        {app.status}
                      </span>
                      <Eye className="view-icon" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
}